// @flow

import R from 'ramda';
import moment from 'moment';

import type { Query } from '../../../main/types/druidTypes';
import { AGENCY_VIEW } from '../viewConstants';

import { ISO_DATE_ANALYTICS } from '../../../main/support/eo-transition-constants';
import { DEFAULT_DATE_FORMAT } from '../../../main/support/dateUtils';

// TODO temporary, as to reduce refactoring noise,
// move common utils here and remove import
export * from '../../../main/support/utils';

export * from './druid/groupby';
export * from './druid/timeseries';
export * from './druid/distribution';
export * from './druid/transformers';
export * from './druid/cohorts';
export * from './druid/events';
export * from './table_helpers';
export * from './eo_helpers';

/**
 * serialize a given object's values as an array
 * @param  {object} value could be an object, array
 * @return {string}
 */
export function serialize(value, sort: ?boolean) {
  return R.pipe(
    R.values,
    sort ? R.curry(v => v.sort()) : R.identity,
    JSON.stringify
  )(value);
}

/**
 * serialize a given object's values as an array
 * by sorting the values provided in the object
 * @param  {object} value could be an object, array
 * @return {string}
 */
export function serializeWithSort(value, sort) {
  return serialize(value, true);
}

/**
 * For a Druid query, makes sure the metric and aggregations are properly set
 * when unique is true
 * @param {Object} existing query
 * @param {boolean} whether unique is true or not
 * @return {Object} new query, containing metric and aggregations if unique
 */
export function applyUnique(query: Query, unique: boolean): Query {
  if (!unique) {
    return query;
  }

  const UNIQUE = 'unique_count';
  return R.merge(query, {
    metric: 'unique_count',
    aggregations: query.aggregations
      ? R.concat(query.aggregations.filter(a => a != UNIQUE), [UNIQUE])
      : [UNIQUE]
  });
}

export function alterQuery(query: Query, unique: boolean): Query {
  const newQuery = applyUnique(query, unique);
  // ----- can remove after EO migration --------
  if (!newQuery && !newQuery.eo_cutoff_date) {
    newQuery.eo_cutoff_date = ISO_DATE_ANALYTICS;
  }
  // ---------------------------------------------
  return newQuery;
}

export function coerceBooleanString(boolStr: string): boolean {
  if (boolStr === 'true') {
    return true;
  }
  return false;
}

/**
 * used for checking if some is on a subdomain for SSO routing purposes
 */
export function isOnSubdomain(host: string): boolean {
  // handle localhost
  if (host.includes('localhost:') || host.includes('127.0.0.1:')) {
    return false;
  }

  const split = host.split('.');

  // not handling local development scenario since local testing doesn't use subdomains and requires mocking headers instead
  if (host.indexOf('dashboard.beta') > -1) {
    // beta uses sso- to mimic a subdomain
    return split[0].indexOf('sso-') > -1;
  }

  // if the first subdomain is not dashboard, we can assume the user is on a subdomain
  return split[0] !== 'dashboard';
}

export function getQueryWithPreviousPeriod(query: Query): Object {
  const startDate = query.start_date;
  const endDate = query.end_date;

  // if the originally query goes back to the cutoff, previous query should request nothing
  if (startDate === ISO_DATE_ANALYTICS) {
    return Object.assign({}, query, {
      start_date: ISO_DATE_ANALYTICS,
      end_date: ISO_DATE_ANALYTICS
    });
  }

  const period = new Date(endDate) - new Date(startDate) + 1;

  const prevStartDate = moment(new Date(startDate) - period).format(
    DEFAULT_DATE_FORMAT
  );
  const prevEndDate = moment(new Date(endDate) - period).format(
    DEFAULT_DATE_FORMAT
  );

  // if prev start date breaches cutoff, don't show any data
  if (new Date(prevStartDate) < new Date(ISO_DATE_ANALYTICS)) {
    return Object.assign({}, query, {
      start_date: ISO_DATE_ANALYTICS,
      end_date: ISO_DATE_ANALYTICS
    });
  }

  return Object.assign({}, query, {
    start_date: prevStartDate,
    end_date: prevEndDate
  });
}

/**
 * Takes an array of strings and turns it into a grammatically correct list,
 * including commas (and Oxford comma) and spaces.
 * @param {Array<string>}
 *      Ex 1: ['apples', 'oranges']
 *      Ex 2: ['apples', 'oranges', 'bananas']
 * @return {string}
 *      Ex 1: 'apples and oranges'
 *      Ex 2: 'apples, oranges, and bananas'
 */
export function createTextListFromArray(array: Array<string>): string {
  let text = array.pop();
  if (array.length) {
    text = `${array.join(', ')}${array.length > 1 ? ',' : ''} and ${text}`;
  }
  return text;
}

export function fillRange(
  start: number,
  end: number,
  step: number = 1
): Array<number> {
  return Array(end - start + 1)
    .fill()
    .map((v, ind) => start + ind);
}

export const removeQueryParam = (url: string, param: string): string => {
  if (!url || !param) {
    return '';
  }

  const base = url.split('?')[0];
  const queryParams = url.split('?')[1];
  if (!queryParams) {
    return url;
  }

  const sanitizedParams = queryParams
    .split('&')
    .filter(p => !p.includes(param))
    .join('&');

  if (!sanitizedParams) {
    return base;
  }

  return `${base}?${sanitizedParams}`;
};

export const generateDashDomain = (location: Object): string => {
  if (
    location.href.includes('dashboard.beta') ||
    location.href.includes('localhost')
  ) {
    return location.href;
  }
  return `${location.protocol}//dashboard.branch.io${location.pathname ||
    ''}${location.search || ''}`;
};

export const findAgencyId = (appsAndOrgs: ?Array<any>): ?string => {
  const agency =
    appsAndOrgs &&
    appsAndOrgs.find(org => org.organization_type === AGENCY_VIEW);
  return agency && agency.organization_id;
};

export const dedupe = (arr: Array<string>) => {
  return arr.filter((item, index) => arr.indexOf(item) >= index);
};

// Consistent time for confirmations throughout the dashboard is 3 seconds
// i.e. message on successfully copying something to the clipboard
export const TIME_TO_DISPLAY_MESSAGE = 3000;

export const DEFAULT_DRUID_RECORD_LIMIT = 5000;

/**
 * method that returns the first occurance of a substring of an error.
 * Primarily used to select a safe substring of an error message to display.
 */
export const getWhitelistedErrorMessage = (
  error: ?string,
  whitelist: Array<string>
) => {
  return whitelist.filter(substring => error && error.includes(substring))[0];
};
